import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import type { PurchaseItemReq, RenewInstanceReq } from '@/api/type-request'
import type { ServerItemInfo } from '@/api/type-server'
import { Button, Loading, message, Modal, Trans } from '@/components'
import {
  BILLING_METHODS,
  type BillingMethod,
  DURATION,
  type Duration,
  INSTANCE_SUBSCRIPTION_RENEWAL,
  ItemPaymentType,
  ItemPriceType,
} from '@/consts/order'
import { TeamRole } from '@/consts/team'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { getBonusStat } from '@/models/credit'
import { Instance } from '@/models/instance'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { AutoRenewalSection } from '@/pages/create-instance/components/AutoRenewalSection'
import { InsufficientRechargeCreditsModal } from '@/pages/create-instance/components/InsufficientRechargeCreditsModal'
import { SubscriptionCreditAlert } from '@/pages/create-instance/components/SubscriptionCreditAlert'
import {
  findSubscriptionPlan,
  getPlanPriceByType,
  getSubscriptionCreditDecision,
  getSubscriptionQuantity,
  getValidPriceCent,
} from '@/pages/create-instance/utils'
import { useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'

import { RenewBillingMethods } from './RenewBillingMethods'
import { RenewPurchaseForSection, type RenewPurchaseValue } from './RenewPurchaseForSection'

interface RenewInstanceModalProps {
  instance: Instance
  onClose: () => void
  onSuccess?: () => void | Promise<void>
}

interface RenewFormValues {
  billingMethod: BillingMethod
  duration: Duration
  purchaseFor: RenewPurchaseValue
  subscriptionTotalDays: number
  autoRenewal: boolean
}

function getInitialDuration(instance: Instance): Duration {
  return instance.order?.item?.paymentType === ItemPaymentType.Available30Days
    ? DURATION.MONTHLY
    : DURATION.DAILY
}

function getResponseError(fallback: string, responseMessage?: string) {
  return responseMessage?.trim() || fallback
}

export function RenewInstanceModal({ instance, onClose, onSuccess }: RenewInstanceModalProps) {
  const { t } = useTranslation()
  const { mode } = useUnifiedContext()
  const userRole = useTeamUserRole()
  const account = useUserStore((state) => state.account)
  const refreshAccount = useUserStore((state) => state.getAccount)
  const isSubscriptionInstance = instance.isSubscription
  const baseOrder = instance.order
  const baseItem = baseOrder?.item

  const [items, setItems] = useState<ServerItemInfo[]>([])
  const [itemsLoading, setItemsLoading] = useState(true)
  const [itemsError, setItemsError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)
  const [insufficientRechargeOpen, setInsufficientRechargeOpen] = useState(false)
  const [rechargeOpen, setRechargeOpen] = useState(false)
  const [formValues, setFormValues] = useState<RenewFormValues>(() => ({
    billingMethod: isSubscriptionInstance
      ? BILLING_METHODS.SUBSCRIPTION
      : BILLING_METHODS.PAY_AS_YOU_GO,
    duration: getInitialDuration(instance),
    purchaseFor: 30,
    subscriptionTotalDays: 30,
    autoRenewal: instance.autoRenewal,
  }))

  const loadItems = useCallback(async () => {
    setItemsLoading(true)
    setItemsError(undefined)

    try {
      const response = await API.getNodeBill({ request_id: instance.displayID }, { noErrMsg: true })

      if (response.code !== 0) {
        setItemsError(
          getResponseError(
            t('instance.renew.loadPlansFailed', {
              defaultValue: 'Unable to load subscription plans. Please try again.',
            }),
            response.msg,
          ),
        )
        return
      }

      setItems(response.items ?? [])
    } catch (error) {
      console.error(error)
      setItemsError(
        t('instance.renew.loadPlansFailed', {
          defaultValue: 'Unable to load subscription plans. Please try again.',
        }),
      )
    } finally {
      setItemsLoading(false)
    }
  }, [instance.displayID, t])

  useEffect(() => {
    void loadItems()
  }, [loadItems])

  const selectedPlan = useMemo(
    () => findSubscriptionPlan(items, formValues.duration),
    [formValues.duration, items],
  )

  const quantity = getSubscriptionQuantity(formValues.duration, formValues.subscriptionTotalDays)

  const estimatedPriceCent = useMemo(() => {
    if (!selectedPlan || quantity <= 0) return undefined

    const samePaymentType = baseItem?.paymentType === selectedPlan.paymentType
    const unitPriceCent = isSubscriptionInstance
      ? ((samePaymentType ? getValidPriceCent(baseOrder?.renewPrice?.priceCent) : undefined) ??
        getPlanPriceByType(selectedPlan, ItemPriceType.Renew) ??
        getPlanPriceByType(selectedPlan, ItemPriceType.Original))
      : (getPlanPriceByType(selectedPlan, ItemPriceType.Original) ??
        getPlanPriceByType(selectedPlan, ItemPriceType.Renew))

    return unitPriceCent === undefined ? undefined : unitPriceCent * quantity
  }, [
    baseItem?.paymentType,
    baseOrder?.renewPrice?.priceCent,
    isSubscriptionInstance,
    quantity,
    selectedPlan,
  ])

  const rechargeCreditCent = getBonusStat(account)?.rechargeBonus.moneyCent
  const rechargeCreditDataAvailable =
    rechargeCreditCent !== undefined &&
    Number.isFinite(rechargeCreditCent) &&
    rechargeCreditCent >= 0
  const canRechargeForSubscription = mode === 'platform' || userRole === TeamRole.Owner
  const subscriptionCreditDecision = getSubscriptionCreditDecision(
    estimatedPriceCent,
    rechargeCreditCent,
    canRechargeForSubscription,
  )
  const subscriptionCreditIsInsufficient =
    subscriptionCreditDecision === 'insufficient_can_recharge' ||
    subscriptionCreditDecision === 'insufficient_cannot_recharge'

  useEffect(() => {
    if (insufficientRechargeOpen && !subscriptionCreditIsInsufficient) {
      setInsufficientRechargeOpen(false)
    }
  }, [insufficientRechargeOpen, subscriptionCreditIsInsufficient])

  const mustSelectSubscription =
    !isSubscriptionInstance && formValues.billingMethod !== BILLING_METHODS.SUBSCRIPTION
  const cannotSubmit =
    submitting ||
    itemsLoading ||
    !!itemsError ||
    mustSelectSubscription ||
    !selectedPlan ||
    quantity <= 0 ||
    estimatedPriceCent === undefined ||
    estimatedPriceCent <= 0 ||
    !rechargeCreditDataAvailable

  const showOperationError = (responseMessage?: string) => {
    message.error(
      getResponseError(
        t('instance.renew.failed', { defaultValue: 'Operation failed, please try again.' }),
        responseMessage,
      ),
    )
  }

  const completeRenewal = async () => {
    await onSuccess?.()
    onClose()
  }

  const handleConfirm = async () => {
    if (cannotSubmit || !selectedPlan || estimatedPriceCent === undefined) return

    if (subscriptionCreditDecision !== 'eligible') {
      if (subscriptionCreditDecision === 'unavailable') return
      setInsufficientRechargeOpen(true)
      return
    }

    setSubmitting(true)

    try {
      if (!isSubscriptionInstance) {
        const payload: RenewInstanceReq = {
          resID: instance.resID,
          status: INSTANCE_SUBSCRIPTION_RENEWAL.status,
          pricePlanReq: {
            pricePlanType: INSTANCE_SUBSCRIPTION_RENEWAL.pricePlanType,
            resID: selectedPlan.resID,
            autoRenewal: formValues.autoRenewal,
            qty: quantity,
          },
        }
        const response = await API.renewInstance(payload, { noErrMsg: true })

        if (response.code !== 0) {
          showOperationError(response.msg)
          return
        }

        message.success(
          t('instance.renew.paygToSubSuccess', {
            defaultValue: 'Successfully switched to subscription.',
          }),
        )
        await completeRenewal()
        return
      }

      if (!baseOrder || !baseItem) {
        message.error(
          t('instance.renew.missingOrder', {
            defaultValue: 'Cannot renew because the order information is missing.',
          }),
        )
        return
      }

      const payload: PurchaseItemReq = {
        res_id: selectedPlan.resID,
        from_order_id: baseOrder.id,
        domain: instance.domain,
        qty: quantity,
        auto_renewal: formValues.autoRenewal,
      }
      const response = await API.purchaseInstanceSubscription(payload, { noErrMsg: true })

      if (response.code !== 0) {
        showOperationError(response.msg)
        return
      }

      message.success(
        t('instance.renew.subscriptionSuccess', {
          defaultValue: 'Subscription renewed successfully.',
        }),
      )
      await completeRenewal()
    } catch (error) {
      console.error(error)
      showOperationError()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal className="w-[640px]" open onClose={submitting ? undefined : onClose}>
        <Modal.Header>
          {isSubscriptionInstance ? (
            <Trans i18nKey="instance.billingMethod.renewTitle">Renew</Trans>
          ) : (
            <Trans i18nKey="instance.billingMethod.billingMethodTitle">Billing Method</Trans>
          )}
        </Modal.Header>

        <Modal.Body className="max-h-[70vh] overflow-y-auto pb-4 scrollbar-hide">
          <RenewBillingMethods
            billingMethod={formValues.billingMethod}
            duration={formValues.duration}
            onBillingMethodChange={(billingMethod) =>
              setFormValues((previous) => ({ ...previous, billingMethod }))
            }
            onDurationChange={(duration) =>
              setFormValues((previous) => ({
                ...previous,
                duration,
                purchaseFor: 30,
                subscriptionTotalDays: 30,
              }))
            }
          />

          <RenewPurchaseForSection
            duration={formValues.duration}
            purchaseFor={formValues.purchaseFor}
            subscriptionTotalDays={formValues.subscriptionTotalDays}
            onChange={({ purchaseFor, totalDays }) =>
              setFormValues((previous) => ({
                ...previous,
                purchaseFor,
                subscriptionTotalDays: totalDays,
              }))
            }
            className="mt-6"
          />

          <AutoRenewalSection
            value={formValues.autoRenewal}
            onChange={(autoRenewal) => setFormValues((previous) => ({ ...previous, autoRenewal }))}
            className="w-full"
          />

          <div className="mt-4">
            <SubscriptionCreditAlert />
          </div>

          {!rechargeCreditDataAvailable ? (
            <div className="mt-4 rounded-lg bg-warning/10 p-3 t-sm text-warning">
              <Trans i18nKey="instance.renew.creditBalanceUnavailable">
                Unable to verify your Recharged Credits balance. Refresh the balance before
                continuing.
              </Trans>
              <Button type="link" className="ml-2 p-0" onClick={() => void refreshAccount()}>
                <Trans i18nKey="instance.renew.refreshBalance">Refresh balance</Trans>
              </Button>
            </div>
          ) : null}

          {itemsLoading ? (
            <div className="flex justify-center py-6">
              <Loading />
            </div>
          ) : null}
          {itemsError ? (
            <div className="mt-4 rounded-lg bg-error/10 p-3 t-sm text-error">
              <p>{itemsError}</p>
              <Button type="link" className="mt-1 p-0" onClick={() => void loadItems()}>
                <Trans i18nKey="common.retry">Retry</Trans>
              </Button>
            </div>
          ) : null}
          {!itemsLoading && !itemsError && !selectedPlan ? (
            <div className="mt-4 rounded-lg bg-warning/10 p-3 t-sm text-warning">
              <Trans i18nKey="instance.renew.noPlan">
                Cannot find a subscription plan for this machine.
              </Trans>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="justify-between">
          <div className="mr-auto flex flex-col">
            <div className="h4 text-highlight">
              {estimatedPriceCent === undefined ? '—' : formatPrice(estimatedPriceCent, false)}
            </div>
            <div className="t-xs text-tertiary">
              <Trans i18nKey="instance.renew.estimatedAmountPayable">
                Estimated amount payable
              </Trans>
            </div>
          </div>
          <Button type="outline" size="md" disabled={submitting} onClick={onClose}>
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button type="secondary" size="md" disabled={cannotSubmit} onClick={handleConfirm}>
            {isSubscriptionInstance ? (
              <Trans i18nKey="instance.renew.submit">Renew</Trans>
            ) : (
              <Trans i18nKey="instance.billingMethod.submit">Confirm</Trans>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {insufficientRechargeOpen && estimatedPriceCent !== undefined ? (
        <InsufficientRechargeCreditsModal
          requiredCredits={String(formatPrice(estimatedPriceCent, false, ''))}
          availableCredits={String(formatPrice(rechargeCreditCent ?? 0, false, ''))}
          onClose={() => setInsufficientRechargeOpen(false)}
          onRecharge={
            subscriptionCreditDecision === 'insufficient_can_recharge'
              ? () => {
                  setInsufficientRechargeOpen(false)
                  setRechargeOpen(true)
                }
              : undefined
          }
        />
      ) : null}

      {rechargeOpen ? (
        <ExchargeModal
          purpose="subscription"
          onClose={() => setRechargeOpen(false)}
          onOk={() => {
            setRechargeOpen(false)
            void refreshAccount()
          }}
        />
      ) : null}
    </>
  )
}
